import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Access modes that can be returned
export const ACCESS_MODES = {
  HIDDEN: 'hidden',
  LOCKED: 'locked',
  READ_ONLY: 'read_only',
  FULL_ACCESS: 'full_access',
};

// Context
const EntitlementContext = createContext({
  sectionAccess: {},
  loading: true,
  error: null,
  refreshAccess: () => {},
  hasAccess: () => false,
  getAccessMode: () => ACCESS_MODES.HIDDEN,
  isLocked: () => true,
  isReadOnly: () => false,
  canWrite: () => false,
});

export const useEntitlements = () => useContext(EntitlementContext);

const PLATFORM_OWNER_FULL_ACCESS = {
  dashboard: 'full_access',
  platform_dashboard: 'full_access',
  agencies: 'full_access',
  users: 'full_access',
  tasks: 'full_access',
  settings: 'full_access',
};

export const EntitlementProvider = ({ children }) => {
  const { isAuthenticated, getAuthHeaders, user, token } = useAuth();
  const [sectionAccess, setSectionAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?.user_id;
  const role = String(user?.role || '').toLowerCase();
  const isPlatformOwnerRole =
    role === 'platform_owner'
    || role === 'super_admin'
    || role === 'platform_admin'
    || user?.is_platform_admin === true;

  const fetchSectionAccess = useCallback(async () => {
    if (!isAuthenticated) {
      setSectionAccess({});
      setLoading(false);
      setError(null);
      return;
    }

    // Platform console roles must not wait on agency-scoped entitlement data.
    if (isPlatformOwnerRole) {
      setSectionAccess({ ...PLATFORM_OWNER_FULL_ACCESS });
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
      const response = await axios.get(`${API}/entitlements/section-access`, {
        headers,
        withCredentials: true,
        timeout: 10000,
      });

      if (response.data.success) {
        setSectionAccess(response.data.section_access || {});
        setError(null);
      } else {
        setError(response.data.error || 'Failed to load access');
        setSectionAccess({});
      }
    } catch (err) {
      console.error('Failed to fetch section access:', err);
      setError(err.message || 'Failed to load access');
      // Don't block the app - default to empty access map
      setSectionAccess({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, isPlatformOwnerRole]);

  useEffect(() => {
    fetchSectionAccess();
  }, [fetchSectionAccess, userId, token]);

  /**
   * Check if user has any level of access to a section
   */
  const hasAccess = useCallback((sectionKey) => {
    const mode = sectionAccess[sectionKey];
    return mode === ACCESS_MODES.READ_ONLY || mode === ACCESS_MODES.FULL_ACCESS;
  }, [sectionAccess]);

  /**
   * Get the access mode for a section
   */
  const getAccessMode = useCallback((sectionKey) => {
    return sectionAccess[sectionKey] || ACCESS_MODES.HIDDEN;
  }, [sectionAccess]);

  /**
   * Check if a section is locked (visible but blocked)
   */
  const isLocked = useCallback((sectionKey) => {
    return sectionAccess[sectionKey] === ACCESS_MODES.LOCKED;
  }, [sectionAccess]);

  /**
   * Check if a section is read-only
   */
  const isReadOnly = useCallback((sectionKey) => {
    return sectionAccess[sectionKey] === ACCESS_MODES.READ_ONLY;
  }, [sectionAccess]);

  /**
   * Check if user can write/modify in a section
   */
  const canWrite = useCallback((sectionKey) => {
    return sectionAccess[sectionKey] === ACCESS_MODES.FULL_ACCESS;
  }, [sectionAccess]);

  const value = {
    sectionAccess,
    loading,
    error,
    refreshAccess: fetchSectionAccess,
    hasAccess,
    getAccessMode,
    isLocked,
    isReadOnly,
    canWrite,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};

/**
 * Higher-order component to wrap sections with access control
 */
export const withAccessControl = (WrappedComponent, sectionKey, options = {}) => {
  const {
    hiddenFallback = null,
    lockedFallback = null,
    readOnlyProps = {},
  } = options;

  return function AccessControlledComponent(props) {
    const { getAccessMode, loading } = useEntitlements();
    
    if (loading) {
      return null; // Or a loading skeleton
    }

    const mode = getAccessMode(sectionKey);

    switch (mode) {
      case ACCESS_MODES.HIDDEN:
        return hiddenFallback;
      
      case ACCESS_MODES.LOCKED:
        return lockedFallback || <LockedSection sectionKey={sectionKey} />;
      
      case ACCESS_MODES.READ_ONLY:
        return <WrappedComponent {...props} {...readOnlyProps} readOnly />;
      
      case ACCESS_MODES.FULL_ACCESS:
      default:
        return <WrappedComponent {...props} />;
    }
  };
};

/**
 * Component to show when a section is locked
 */
export const LockedSection = ({ sectionKey, title, message }) => {
  return (
    <div 
      className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200"
      data-testid={`locked-section-${sectionKey}`}
    >
      <div className="p-3 rounded-full bg-amber-100 mb-4">
        <svg 
          className="w-8 h-8 text-amber-600" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-stone-900 mb-2">
        {title || 'Feature Locked'}
      </h3>
      <p className="text-sm text-slate-500 text-center max-w-sm mb-4">
        {message || 'This feature is not available in your current plan. Upgrade to unlock access.'}
      </p>
      <button 
        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
        onClick={() => window.location.href = '/settings/billing'}
      >
        Upgrade Plan
      </button>
    </div>
  );
};

/**
 * Component to conditionally render based on access
 */
export const AccessGate = ({ 
  sectionKey, 
  children, 
  fallback = null,
  showLocked = true,
  lockedTitle,
  lockedMessage,
}) => {
  const { getAccessMode, loading } = useEntitlements();

  if (loading) {
    return null;
  }

  const mode = getAccessMode(sectionKey);

  if (mode === ACCESS_MODES.HIDDEN) {
    return fallback;
  }

  if (mode === ACCESS_MODES.LOCKED) {
    if (showLocked) {
      return <LockedSection sectionKey={sectionKey} title={lockedTitle} message={lockedMessage} />;
    }
    return fallback;
  }

  // read_only or full_access - render children
  return children;
};

export default EntitlementContext;
